import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import {
  CROPPER_MODES,
  FileErrorsPipe,
  IMAGE_FILE_TYPES,
  ImageCropperDefaultConfiguration,
} from '@certemy/ui-components';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ComputedFrom } from '@certemy/common';
import {
  EntityForImageUpdate,
  IMAGE_SIZE_RESTRICTION,
  UPLOAD_ENTITY_NAME_MAP,
  UPLOAD_IMAGE_ACTION,
} from 'components/upload-image-modal/upload-image-modal.constants';
import _get from 'lodash-es/get';

@Component({
  selector: 'cm-upload-image-modal',
  templateUrl: './upload-image-modal.component.html',
  styleUrls: ['./upload-image-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadImageModalComponent {
  @Input() defaultConfiguration: ImageCropperDefaultConfiguration;
  @Input() entity: EntityForImageUpdate = EntityForImageUpdate.ORGANIZATION;

  @Input() isResetAvailable = true;
  @Input() allowSwitchingMode = false;
  @Input() resetScreenMessage = '';
  @Input() options = {};
  @Input() availableModes = [CROPPER_MODES.SQUARE, CROPPER_MODES.CIRCLE];

  @ComputedFrom('entity')
  get entityName(): string {
    return UPLOAD_ENTITY_NAME_MAP[this.entity];
  }
  isResetScreen = false;

  @ComputedFrom('options')
  get cropperOptions() {
    return {
      autoCropArea: 1,
      ...this.options,
    };
  }

  allowedTypes = IMAGE_FILE_TYPES.filter((type) => type !== '.gif');
  loading = false;
  imageFile: File;
  showFileError: boolean;
  maxLogoFileSize = 3;
  UPLOAD_IMAGE_ACTION = UPLOAD_IMAGE_ACTION;

  private _croppedImageEvent: any;

  get modalTitle() {
    if (this.isResetScreen) {
      return 'Confirm changes';
    }
    if (this.imageFile && !this.loading) {
      return `Crop ${this.entityName}`;
    } else if (_get(this.defaultConfiguration, 'originalFile')) {
      return `Edit ${this.entityName}`;
    }

    return `Load ${this.entityName}`;
  }

  constructor(
    private activeModal: NgbActiveModal,
    private fileErrorsPipe: FileErrorsPipe,
    private changeDetector: ChangeDetectorRef,
  ) {}

  onFilesChange(files: File[]) {
    const file = files[files.length - 1];
    this.showFileError = false;
    const img = new Image();
    img.onload = () => {
      const correctImageResolution =
        img.width >= IMAGE_SIZE_RESTRICTION.minWidth && img.height >= IMAGE_SIZE_RESTRICTION.minHeight;

      this.showFileError =
        !correctImageResolution || !!this.fileErrorsPipe.transform(file, this.maxLogoFileSize, this.allowedTypes);

      if (!this.showFileError) {
        this.loading = true;
        this.imageFile = file;
      }
      this.changeDetector.markForCheck();
    };

    img.src = URL.createObjectURL(file);
  }

  imageLoaded() {
    this.loading = false;
  }

  imageCropped(event: any) {
    this._croppedImageEvent = event;
  }

  submit(action: UPLOAD_IMAGE_ACTION) {
    this.activeModal.close({
      config: {
        ...this._croppedImageEvent,
        originalImageFile: this.imageFile || this.defaultConfiguration.originalFile,
      },
      action,
    });
  }

  cancel() {
    this.activeModal.dismiss();
  }

  back() {
    this.imageFile = null;
  }

  setResetScreenMode() {
    this.isResetScreen = true;
  }

  reset() {
    this.activeModal.close({
      config: null,
      action: UPLOAD_IMAGE_ACTION.RESET,
    });
  }

  backToEdit() {
    this.isResetScreen = false;
  }
}
